import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActionEventArgs, AgendaService, DayService, DragAndDropService, EventRenderedArgs, EventSettingsModel, GroupModel, MonthService, ResizeService, ScheduleComponent, ScheduleModule, TimeScaleModel, TimelineMonthService, TimelineViewsService, View, WeekService, WorkWeekService } from '@syncfusion/ej2-angular-schedule';
import { BehaviorSubject, Observable, Subject, Subscription, debounceTime, filter, finalize, interval, of, switchMap, take, takeWhile, tap } from 'rxjs';
import { ILocation } from 'src/app/shared/models/location/location.model';
import { ISession } from 'src/app/shared/models/session-group/session.model';
import { IShopItem } from 'src/app/shared/models/shop-item/shop-item.model';
import { ICallablesSessionsGetForLocationPickerData, ICallablesSessionsGetForLocationPickerResult, SessionsService } from 'src/app/shared/services/entities/sessions/sessions.service';
import { IStartAtEndAtFG } from '../../session-card-modal/start-at-end-at-input/start-at-end-at-input.component';
import { SchedulerLocalizeService } from './scheduler-localize.service';
import { timeStringToDate } from 'src/app/shared/components/time-input/time-input.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { LocationModalComponent } from 'src/app/shared/modals/location-modal/location-modal.component';
import { Operation } from 'src/app/shared/enums/utils/operations.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicStyleLoaderService } from 'src/app/shared/services/dynamic-style-loader.service';
import { JsonPipe } from '@angular/common';
import { IUserPreference, IUserPreferencesData } from 'src/app/shared/models/user-preference/user-preference.model';
import { UserPreferencesStoreService } from 'src/app/shared/services/store/user-preferences-store.service';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

interface ISessionColumnData {
  Id: number;
  Subject: string;
  StartTime: Date;
  EndTime: Date;
  IsAllDay: boolean;
  color: string;
  locationId: number;
  locationGroupId: number;
  allowResizing: boolean;
  allowDragDrop: boolean;
}
interface ILocationSource {
  id: number;
  title: string;
  color: string;
  locationGroupId: number;
}
interface ILocationGroupSource {
  id: number;
  title: string;
  expanded: boolean;
  pinned: boolean;
  pinnable: boolean;
  pinMovableDirections?: ('up' | 'down')[];
  cssClass?: string;
  thisSessionIsPresent? : boolean; // if this group contains session that is currently being edited
}

export interface ITimelineSessionChange {
  id: number;
  startAt: Date;
  endAt: Date;
  locationId: number;
}

  
export const TIMELINE_newLocationId = -4321; // dont change, can be saved in user preferences
const TIMELINE_defaultLocationGroupId = -42; // it does not work with null...

@Component({
  selector: 'app-timeline',
  standalone: true,
  imports: [
    ScheduleModule, TranslateModule,
    NgbTooltipModule
  ],
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.scss',
  providers: [
    TimelineViewsService,
    TimelineMonthService,
    DayService, WeekService, WorkWeekService, MonthService, AgendaService, DragAndDropService, ResizeService,
  ],
  encapsulation: ViewEncapsulation.None
})
export class TimelineComponent implements OnInit, OnDestroy, AfterViewInit {
  TIMELINE_newLocationId = TIMELINE_newLocationId;

  @Input({required: true}) selectedDate: Date | undefined;
  _locations: ILocation[] = [];
  get locations() { return this._locations; }
  @Input({required: true}) set locations(val: ILocation[]) {
    this._locations = val;
    this.lazyLoad$.next(true);
  }
  @Input({required: true}) session: ISession | undefined;
  @Input({required: true}) sessionLocationId: number | undefined;
  @Input({required: true}) sessionDates: FormGroup<IStartAtEndAtFG> | undefined;
  @Input({required: true}) shopItem: IShopItem | undefined;
  @Input({required: true}) set locationIdsToShow(val: number[]) { this.locationData = this.locationDataSource.filter(x => val.includes(x.id) || x.id === TIMELINE_newLocationId); };
  @Input({required: true}) datesChanged$: Subject<{ startAt: Date, endAt: Date}> | undefined;
  @Input({required: true}) locationChanged$: Subject<number | null> | undefined;
  @Input({required: true}) userPreferences: Partial<IUserPreferencesData> | null = null;
  @Output() sessionChange = new EventEmitter<ITimelineSessionChange>();

  @ViewChild('schedule') schedule: ScheduleComponent | undefined;

  private lazyLoad$ = new BehaviorSubject<boolean>(false);

  locationsDataIn: ICallablesSessionsGetForLocationPickerResult | null = null;

  eventSettings: EventSettingsModel = {
    dataSource: [],
    enableTooltip: true,
  };
  timeScale: TimeScaleModel = {
    enable: true,
    interval: 60,
    slotCount: 4,
    
  }
  group: GroupModel = {
    resources: ['LocationGroups', 'Locations'],
    enableCompactView: false,
  }
  locationGroupsDataSource: ILocationGroupSource[] = [];
  locationDataSource: ILocationSource[] = []; // source, do no change
  locationData: ILocationSource[] = []; // filtered 
  currentView: View = 'TimelineWeek';
  currentSessionColor = '#1ea97c';
  isCellClick = false;
  newLocationModalOpened = false;
  fetchingLocationsData = false;
  initScrolled = false;
  expandedLocationGroupIds: number[] = [];
  disableGroupHeaderTooltip = false;

  subs: Subscription[] = [];

  constructor(
    private sessionsService: SessionsService,
    private modalService: ModalService,
    private translate: TranslateService,
    schedulerLocalize: SchedulerLocalizeService, // keep this here, otherwise the scheduler will not be localized
    private dynamicStyleLoader: DynamicStyleLoaderService,
    private userPreferencesStore: UserPreferencesStoreService,
  ) {
    this.dynamicStyleLoader.loadStyles([
      '/syncfusion/ej2-base/styles/material.css',
      '/syncfusion/ej2-buttons/styles/material.css',
      '/syncfusion/ej2-calendars/styles/material.css',
      '/syncfusion/ej2-dropdowns/styles/material.css',
      '/syncfusion/ej2-inputs/styles/material.css',
      '/syncfusion/ej2-lists/styles/material.css',
      '/syncfusion/ej2-popups/styles/material.css',
      '/syncfusion/ej2-navigations/styles/material.css',
      '/syncfusion/ej2-schedule/styles/material.css'
    ]).then(() => {
      this.schedule?.refresh();
    })
  }


  ngOnInit(): void {
    this.subs.push(
      this.datesChanged$?.subscribe(({ startAt, endAt }) => {
        if (!this.session) return;

        this.updateSessionInSchedule(this.session.id, { StartTime: startAt, EndTime: endAt }, false);
      }) ?? Subscription.EMPTY,

      this.locationChanged$?.subscribe(locationId => {
        if (!this.session) return;
        this.updateSessionInSchedule(this.session.id, { locationId: locationId ?? undefined }, false);
      }) ?? Subscription.EMPTY,


    );
  }

  ngAfterViewInit(): void {
    this.subs.push(
      this.lazyLoad$.pipe(
        debounceTime(200),
        switchMap((val) => {
          if (val) {
            return this.fetchLocationsData() ?? of(null);
          } else {
            return of(null);
          }
        })
      ).subscribe({
        next: (r) => {
          this.locationsDataIn = r;
          this.createData();
        }
      })
    );
  }

  private fetchLocationsData() {
    // there has to be bug in ej2-schedule, because when activeView is accessed early, it is null
    // so try to wait until it is not null
    return interval(200).pipe( // Poll every 200 ms
      takeWhile(() => this.schedule?.activeView === null, true), // Continue until activeView is not null
      filter(() => this.schedule?.activeView !== null), // Emit only when activeView is available
      take(1), // Only take the first non-null emission
      switchMap(() => {
        const data: ICallablesSessionsGetForLocationPickerData = {
          locationIds: this.locations.map(location => location.id),
          startAt: this.schedule?.activeView.getStartDate?.().toUTCLikeDate().toISOString()!,
          endAt: this.schedule?.activeView.getEndDate?.().toUTCLikeDate().toISOString()!,
        };
        this.fetchingLocationsData = true;
        return this.sessionsService.getForLocationPicker(data).pipe(
          finalize(() => this.fetchingLocationsData = false)
        );
      })
    );
  }

  createData() {
    if (!this.locationsDataIn) return;
    this.locationDataSource = [
      ...this.locationsDataIn.locations.map(location => ({ title: (location.internalTitle || location.title), id: location.id, color: '#bada55', locationGroupId: location.locationGroup?.id ?? TIMELINE_defaultLocationGroupId })),
      {
        id: TIMELINE_newLocationId,
        title: ' ',
        locationGroupId: TIMELINE_newLocationId,
        color: '#bada55'
      }
    ]
    this.locationData = this.locationDataSource;

    const locationsWithGroups = this.locationsDataIn.locations.filter(location => location.locationGroup !== null);
    const locationsWithoutGroup = this.locationsDataIn.locations.filter(location => location.locationGroup === null);
    let locationGroups: ILocationGroupSource[] = [];
    if (locationsWithGroups.length > 0) {
      locationGroups.push(
        ...locationsWithGroups.map(location => ({
            title: location.locationGroup!.title,
            id: location.locationGroup!.id,
            pinned: this.userPreferences?.admin_locationPicker_pinnedLocationGroups?.includes(location.locationGroup!.id) ?? false,
            pinnable: true,
            expanded:
              this.expandedLocationGroupIds.includes(location.locationGroup!.id) ||
              location.sessions.some(session => session.id === this.session?.id) ||
              location.locationGroup?.external === false, // expand group if session is in it
            thisSessionIsPresent: location.sessions.some(session => session.id === this.session?.id),
          } )
        )
      );
    }
    // remove group duplicates - TODO - nevhodne udelane, protoze vys nastavuju napr expanded podle setkani a filtrem unikatnosti pak můžu přijít o to, že se nastavena skupina ztrati
    // tzn preferovat ponechani objeku s thisSessionIsPresent
    locationGroups = locationGroups.filter((group, index, self) =>
      index === self.findIndex((g) => g.id === group.id && (g.thisSessionIsPresent || !self.some(x => x.id === group.id && x.thisSessionIsPresent)))
    );

    // add default group for locations without group
    if (locationsWithoutGroup.length > 0) {
      const defaultGroupSource: ILocationGroupSource = {
        title: this.translate.instant('admin.location-picker-modal.no-group'),
        id: TIMELINE_defaultLocationGroupId,
        pinned: this.userPreferences?.admin_locationPicker_pinnedLocationGroups?.includes(TIMELINE_defaultLocationGroupId) ?? false,
        pinnable: true,
        expanded: 
          locationsWithGroups.length === 0 || // if there are no groups, expand default group  
          this.expandedLocationGroupIds.includes(TIMELINE_defaultLocationGroupId) ||
          locationsWithoutGroup.flatMap(x => x.sessions).some(session => session.id === this.session?.id), // expand group if session is in it
        thisSessionIsPresent: locationsWithoutGroup.flatMap(x => x.sessions).some(session => session.id === this.session?.id)
      } 
      locationGroups.push(defaultGroupSource);
      if (defaultGroupSource.expanded) this.expandedLocationGroupIds.push(defaultGroupSource.id);
    }
    // allways add 'New location' option
    locationGroups.push({ title: this.translate.instant('admin.location-picker-modal.new-location'), id: TIMELINE_newLocationId, expanded: false, pinned: false, pinnable: false, cssClass: 'no-toggle' });

    
    this.locationGroupsDataSource = locationGroups;
    
    this.sortLocationGroups();
    this.initPinMovableDirections();


    // because of known bug in ej2-schedule, we need to create dummy event for each empty location
    // https://ej2.syncfusion.com/javascript/documentation/schedule/frequently-asked-questions
    for (const location of this.locationsDataIn.locations) {
      if (location.sessions.length === 0) {
        location.sessions.push({
          id: -1,
          title: ' ',
          startAt: new Date(1970).toISOString(),
          endAt: new Date(1970).toISOString(),
          shopItem: {
            id: -1,
            title: ' '
          }
        })
      }
    }
    // /

    const currentEvents = this.schedule?.getEvents();
    if (currentEvents) {
      this.schedule?.deleteEvent(currentEvents);
    }
    const eventsToSet = this.locationsDataIn.locations.flatMap(location => location.sessions.map(session => {
      const startAtDate = this.session?.id === session.id ? timeStringToDate(this.sessionDates?.value.startAt!, this.sessionDates?.value.startAtTime!, true)! : new Date(session.startAt);
      const endAtDate = this.session?.id === session.id ? timeStringToDate(this.sessionDates?.value.endAt!, this.sessionDates?.value.endAtTime!, true)! : new Date(session.endAt);
      const data: ISessionColumnData = {
        Id: session.id,
        Subject: session.title ?? session.shopItem.title,
        StartTime: new Date(startAtDate.getUTCFullYear(), startAtDate.getUTCMonth(), startAtDate.getUTCDate(), startAtDate.getUTCHours(), startAtDate.getUTCMinutes(), startAtDate.getUTCSeconds(), startAtDate.getUTCMilliseconds()),
        EndTime:  new Date(endAtDate.getUTCFullYear(), endAtDate.getUTCMonth(), endAtDate.getUTCDate(), endAtDate.getUTCHours(), endAtDate.getUTCMinutes(), endAtDate.getUTCSeconds(), endAtDate.getUTCMilliseconds()),
        IsAllDay: false,
        color: this.session?.id === session.id ? this.currentSessionColor :
          this.shopItem?.id === session.shopItem.id ? '#74caae' : '#818ea1',
        locationId: location.id,
        locationGroupId: location.locationGroup?.id ?? TIMELINE_defaultLocationGroupId,
        allowDragDrop: this.session?.id === session.id,
        allowResizing: this.session?.id === session.id
      }

      return data;
    }));
    for (const d of eventsToSet) {
      this.schedule?.addEvent(d);
    }

    const isThisSessionInData = eventsToSet.find(x => x.Id === this.session?.id);
    if (!isThisSessionInData && this.session) {
      // kvuli tomu jake data se stahuji (sessions vsech lokaci), tak tu bude chybet setkani, ktere nema lokaci
      // takze tady je potreba pridat
      const startAtDate = timeStringToDate(this.sessionDates?.value.startAt!, this.sessionDates?.value.startAtTime!)!;
      const endAtDate = timeStringToDate(this.sessionDates?.value.endAt!, this.sessionDates?.value.endAtTime!)!;
      const sessionToAdd: ISessionColumnData = {
        Id: this.session.id,
        Subject: this.session?.title ?? this.shopItem?.title ?? ' ',
        StartTime: startAtDate,// new Date(startAtDate.getUTCFullYear(), startAtDate.getUTCMonth(), startAtDate.getUTCDate(), startAtDate.getUTCHours(), startAtDate.getUTCMinutes(), startAtDate.getUTCSeconds(), startAtDate.getUTCMilliseconds()),
        EndTime:  endAtDate,//new Date(endAtDate.getUTCFullYear(), endAtDate.getUTCMonth(), endAtDate.getUTCDate(), endAtDate.getUTCHours(), endAtDate.getUTCMinutes(), endAtDate.getUTCSeconds(), endAtDate.getUTCMilliseconds()),
        IsAllDay: false,
        color: this.currentSessionColor,
        locationId: this.sessionLocationId ?? -1,
        locationGroupId: this.locations.find(x => x.id === this.sessionLocationId)?.locationGroup?.id ?? TIMELINE_defaultLocationGroupId,
        allowDragDrop: true,
        allowResizing: true
      };
      this.schedule?.addEvent(sessionToAdd);
    }

    if (!this.initScrolled) {
      this.initScrolled = true;

      this.schedule?.dataBound.pipe(debounceTime(50), take(1)).subscribe(() => {
        let dateToScrollTo = this.selectedDate ?? new Date();
        if (dateToScrollTo.getHours() > 2) dateToScrollTo = dateToScrollTo.addSeconds(-1 * 60 * 60); 
        this.schedule?.scrollTo(this.formatTime(dateToScrollTo));
        if (this.sessionLocationId) this.schedule?.scrollToResource(this.sessionLocationId)
      });
    }
  }

  private initPinMovableDirections() {
    const locationGroups = this.locationGroupsDataSource;
    for (let i = 0; i < locationGroups.length; i++) {
      const group = locationGroups[i];
      const prevGroup = locationGroups.at(i-1);
      const nextGroup = locationGroups.at(i+1);
      const pinMovableDirections: ('up' | 'down')[] = [];
      if (group.pinned) {
        if (nextGroup?.pinned) {
          pinMovableDirections.push('down'); // can move down if next group is pinned
        }
        if (prevGroup?.pinned) {
          pinMovableDirections.push('up'); // can move up if previous group is pinned
        }
      }
      group.pinMovableDirections = pinMovableDirections;
    }
  }

  private sortLocationGroups() {
    // sort location groups
    // first show pinned groups, and those pinned sort by position in this.userPreferences.admin_locationPicker_pinnedLocationGroups
    // then sort by title
    // make sure, that TIMELINE_newLocationId is always at the end of the list
    const newLocationGroup = this.locationGroupsDataSource.find(x => x.id === TIMELINE_newLocationId);
    this.locationGroupsDataSource = this.locationGroupsDataSource
      .filter(x => x.id !== TIMELINE_newLocationId) // remove new location group from the list
      .sort((a, b) => {
        if (a.pinned && !b.pinned) return -1; // a is pinned, b is not, a comes first
        if (!a.pinned && b.pinned) return 1; // b is pinned, a is not, b comes first
        if (a.pinned && b.pinned) {
          // both are pinned, sort by position in user preferences
          const aIndex = this.userPreferences?.admin_locationPicker_pinnedLocationGroups?.indexOf(a.id) ?? -1;
          const bIndex = this.userPreferences?.admin_locationPicker_pinnedLocationGroups?.indexOf(b.id) ?? -1;
          if (aIndex === -1 && bIndex === -1) return 0; // both are not in user preferences, keep original order
          if (aIndex === -1) return 1; // a is not in user preferences, b comes first
          if (bIndex === -1) return -1; // b is not in user preferences, a comes first
          return aIndex - bIndex; // sort by index in user preferences
        }
        return a.title.localeCompare(b.title); // both are not pinned, sort by title
      }); // add new location group at the end
    if (newLocationGroup) {
      this.locationGroupsDataSource.push(newLocationGroup);
    }

  }

  onPin(locationGroupId: number, e: Event) {
    e.stopPropagation();

    const index = this.locationGroupsDataSource.findIndex(x => x.id === locationGroupId);
    if (index === -1) return;
    const locationGroup = this.locationGroupsDataSource[index];
    if (locationGroup.pinnable) {
      locationGroup.pinned = !locationGroup.pinned;
      if (locationGroup.pinned) {
        this.expandedLocationGroupIds.push(locationGroupId);
      } else {
        this.expandedLocationGroupIds = this.expandedLocationGroupIds.filter(x => x !== locationGroupId);
      }
      this.locationGroupsDataSource = [...this.locationGroupsDataSource]; // trigger change detection
      this.sortLocationGroups();
      this.initPinMovableDirections();

      this.savePinPreferences();
    }
  }
  onMovePin(direction: 'up' | 'down', locationGroupId: number, e: Event) {
    e.stopPropagation();
    
    const index = this.locationGroupsDataSource.findIndex(x => x.id === locationGroupId);
    if (index === -1) return;
    if (direction === 'up' && index > 0) {
      // move up
      const temp = this.locationGroupsDataSource[index - 1];
      this.locationGroupsDataSource[index - 1] = this.locationGroupsDataSource[index];
      this.locationGroupsDataSource[index] = temp;
    } else if (direction === 'down' && index < this.locationGroupsDataSource.length - 1) {
      // move down
      const temp = this.locationGroupsDataSource[index + 1];
      this.locationGroupsDataSource[index + 1] = this.locationGroupsDataSource[index];
      this.locationGroupsDataSource[index] = temp;
    }
    this.locationGroupsDataSource = [...this.locationGroupsDataSource]; // trigger change detection
    this.initPinMovableDirections();
    this.savePinPreferences();
  }
  private savePinPreferences() {
    // save user preferences
    this.userPreferencesStore.upsert({
      admin_locationPicker_pinnedLocationGroups: this.locationGroupsDataSource
        .filter(x => x.pinned).map(x => x.id)
    })?.pipe(take(1)).subscribe();
  }

  // custom event color
  onEventRendered(args: EventRenderedArgs): void {
    const event = args.data as ISessionColumnData;
    if (event.color) {
      (args.element as HTMLElement).style.backgroundColor = event.color;

      if (event.Id === this.session?.id) {
        (args.element as HTMLElement).style.boxShadow = `0 0 10px 0px ${event.color}`;
        (args.element as HTMLElement).classList.add('pulse');
      }
      
    }
  }

  onDragStart(args: any) {
    if (args.data.allowDragDrop !== true) {
      args.cancel = true;
    } else {
      args.interval = 5;
    }
  }
  onDragStop(args: any & { data: ISessionColumnData }) {
    const data: ISessionColumnData = args.data;
    this.checkAndHandleNewLocationAction(data.locationId);

    this.sessionChange.emit({
      id: data.Id,
      startAt: data.StartTime,
      endAt: data.EndTime,
      locationId: data.locationId,
    });
  }
  onResizeStart(args: any) {
    if (args.data.allowResizing !== true) {
      args.cancel = true;
    } else {
      args.interval = 5;
    }
  }
  onResizeStop(args: any & { data: ISessionColumnData }) {
    const data: ISessionColumnData = args.data;
    this.checkAndHandleNewLocationAction(data.locationId);

    this.sessionChange.emit({
      id: data.Id,
      startAt: data.StartTime,
      endAt: data.EndTime,
      locationId: data.locationId
    });
  }

  onSelect(args: any) {
    setTimeout(() => {
      // bcs cellClick and select are fired at the same time, we need to ignore this select event if cellClick was fired too (it sets this flag to true)
      // and bcs they are fired at the same time, we need to wait a bit before we can be sure that the cellClick set the flag
      if (this.isCellClick) {
        this.isCellClick = false; // Reset the flag
        return; // Ignore this event
      }
      if (args.requestType === 'cellSelect' && args.name === 'select' && args.data) {
        const data: Partial<ISessionColumnData> = args.data;
        if (data.locationId) this.checkAndHandleNewLocationAction(data.locationId);

        this.updateSessionInSchedule(this.session?.id!, data, true);
      }
    },69);
  }

  updateSessionInSchedule(sessionId: number, data: Partial<ISessionColumnData>, emitChange: boolean) {
    let sessionToUpdate: ISessionColumnData = Object.values(this.eventSettings.dataSource ?? {}).find(x => x.Id === sessionId)!;

    sessionToUpdate.StartTime = data.StartTime ?? sessionToUpdate.StartTime;
    sessionToUpdate.EndTime = data.EndTime ?? sessionToUpdate.EndTime;
    sessionToUpdate.locationId = data.locationId ?? sessionToUpdate.locationId;
    sessionToUpdate.locationGroupId = data.locationGroupId ?? sessionToUpdate.locationGroupId;

    this.schedule?.saveEvent(sessionToUpdate, "Save");

    if (emitChange) {
      this.sessionChange.emit({
        id: sessionToUpdate.Id,
        startAt: sessionToUpdate.StartTime,
        endAt: sessionToUpdate.EndTime,
        locationId: sessionToUpdate.locationId
      });
    }
  }

  onEventDoubleClick(args: EventRenderedArgs): void {
    args.cancel = true; // Prevent the edit modal from opening
  }
  onCellDoubleClick(args: ActionEventArgs): void {
    args.cancel = true; // Prevent the edit modal from opening
  }

  onCurrentViewChange(args: any) {
    this.lazyLoad$.next(true);
  }

  onNavigating(args: any) {
    this.lazyLoad$.next(true);
  }

  onSchedulerClick(e: Event) {
    // if clicked on resource cell, update session location
    
    // find nearest element with data-group-index attribute using selector
    let target = e.target as HTMLElement | null;
    while (target) {
      if (target.matches('.e-appointment')) return;
      if (target.matches('[data-group-index]')) {
        // get the value of data-group-index attribute
        const groupIndex = target.getAttribute('data-group-index');
        
        // get group by index
        const group = this.schedule?.getResourcesByIndex(parseInt(groupIndex!));
        
        const locationId = group?.resourceData['id'];
        const locationGroupId = group?.resourceData['locationGroupId'];
        
        this.checkAndHandleNewLocationAction(locationId);
        if (locationId && locationGroupId) {

          this.updateSessionInSchedule(this.session?.id!, { locationId, locationGroupId }, true);
        } else if (locationGroupId === undefined) {
          // clicked on location Group, so lets toggle its expanded state
          const locationGroup = this.locationGroupsDataSource.find(x => x.id === group?.groupData?.['locationGroupId']);
          if (locationGroup && locationGroup.id !== TIMELINE_newLocationId) {
            this.toggleGroupExpanded(locationGroup.id);
          }
        }
        break;
      };
      target = target.parentElement;
    }
  }

  private toggleGroupExpanded(locationGroupId: number) {
    const locationGroup = this.locationGroupsDataSource.find(x => x.id === locationGroupId);
    this.locationGroupsDataSource = [...this.locationGroupsDataSource]; // trigger change detection

    if (locationGroup) {
      locationGroup.expanded = !locationGroup.expanded;
      if (locationGroup.expanded) {
        this.expandedLocationGroupIds.push(locationGroupId);
      } else {
        this.expandedLocationGroupIds = this.expandedLocationGroupIds.filter(x => x !== locationGroupId);
      }
    }
  }

  onCellClick(args: { startTime: Date, endTime: Date, groupIndex: number, cancel: boolean }) {
    this.isCellClick = true;
    if (args.cancel) return;

    const locationId = this.schedule?.getResourcesByIndex(args.groupIndex).resourceData['id'];
    this.checkAndHandleNewLocationAction(locationId);

    const milisecondsDiff = (timeStringToDate(this.sessionDates?.value.endAt!, this.sessionDates?.value.endAtTime!)?.getTime() ?? 0) - (timeStringToDate(this.sessionDates?.value.startAt!, this.sessionDates?.value.startAtTime!)?.getTime() ?? 0);
    this.updateSessionInSchedule(this.session?.id!, { StartTime: args.startTime, EndTime: args.startTime.addUTCMiliSeconds(milisecondsDiff) }, true);
  }

  checkAndHandleNewLocationAction(locationId: number) {
    if (locationId === TIMELINE_newLocationId) {
      if (this.newLocationModalOpened) {
        // prevent multiple modals because of multiple actions at same time
        return;
      };
      this.newLocationModalOpened = true;
      setTimeout(() => { this.newLocationModalOpened = false; }, 42);
      const res = this.modalService.openLocationModal({
        c: LocationModalComponent,
        operation: Operation.CREATE
      });
      res.pipe(take(1)).subscribe({
        next: (r) => {
          // insert new location to the list at předposlední pozici
          this.locationDataSource.splice(this.locationDataSource.length - 1, 0, { title: r.data.internalTitle || r.data.title, id: r.data.id, color: '#bada55', locationGroupId: r.data.locationGroupId ?? TIMELINE_defaultLocationGroupId });
          this.locationData = this.locationDataSource;

          this.updateSessionInSchedule(this.session?.id!, { locationId: r.data.id, locationGroupId: r.data.locationGroupId ?? undefined }, true);
          
          const locationGroup = this.locationGroupsDataSource.find(x => x.id === r.data.locationGroupId);
          if (locationGroup) {
            this.toggleGroupExpanded(locationGroup.id);
          }
        }
      })
    }
  }

  private formatTime(date: Date): string {
    // Get hours and minutes
    let hours: number | string = date.getHours();
    let minutes: number | string = date.getMinutes();
    // Add leading zero if needed
    if (hours < 10) hours = '0' + hours;
    if (minutes < 10) minutes = '0' + minutes;
    // Format hh:mm
    return `${hours}:${minutes}`;
  }


  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

}
